import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import type { BranchingStory, BranchNode } from '@/types/storytelling'

export interface BranchSessionState {
  story: BranchingStory
  currentNodeId: string
  currentNode: BranchNode
  path: string[]
  isComplete: boolean
}

/**
 * Get a node from a branching story.
 */
export function getNode(story: BranchingStory, nodeId: string): BranchNode | undefined {
  return story.nodes[nodeId]
}

/**
 * Process a choice and return the next node ID.
 * Returns undefined if the choice is invalid.
 */
export function makeChoice(
  story: BranchingStory,
  currentNodeId: string,
  choiceId: string
): string | undefined {
  const node = story.nodes[currentNodeId]
  if (!node?.choices) return undefined

  const choice = node.choices.find((c) => c.id === choiceId)
  if (!choice) return undefined

  // The choice ID is the next node ID
  return story.nodes[choiceId] ? choiceId : undefined
}

/**
 * Check if a node is an ending.
 */
export function isEnding(story: BranchingStory, nodeId: string): boolean {
  return story.nodes[nodeId]?.isEnding === true
}

/**
 * Compute session state from a path of choice IDs.
 * Pure function.
 */
export function getSessionState(story: BranchingStory, path: string[]): BranchSessionState {
  const currentNodeId = path.length > 0 ? path[path.length - 1] : story.startNodeId
  const currentNode = story.nodes[currentNodeId]

  return {
    story,
    currentNodeId,
    currentNode,
    path,
    isComplete: currentNode?.isEnding === true,
  }
}

/**
 * Save a completed branching story session.
 */
export async function saveSession(
  childId: string,
  storyId: string,
  pathTaken: string[]
): Promise<boolean> {
  if (!isSupabaseConfigured()) return false

  const { error } = await supabase.from('branching_story_sessions').insert({
    child_id: childId,
    story_id: storyId,
    path_taken: pathTaken,
  })

  if (error) {
    console.error('Failed to save branching story session:', error)
    return false
  }

  return true
}
